import { Dialog } from '@headlessui/react';
import { useEffect, useId, useRef } from 'react';
import Button from '../../../basic/button';
import { useTranslator } from '../../header/locales-provider';
import {
  focusModalContent,
  isolateModalForAssistiveTechnology,
} from '../../../../utils/assistiveTechnologyModalIsolation';

interface LogoutNotificationModalProps {
  isOpen: boolean;
  handleClose: () => void;
  onSelected: () => void;
}
export default function LogoutNotificationModal({
  isOpen,
  handleClose,
}: LogoutNotificationModalProps): JSX.Element {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const locales = useTranslator('global');

  useEffect(() => {
    if (!isOpen) return;

    const timerId = window.setTimeout(() => {
      focusModalContent(panelRef.current);
    }, 0);

    return () => window.clearTimeout(timerId);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    return isolateModalForAssistiveTechnology(panelRef.current);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as Node | null;

      if (!panelRef.current || !target || panelRef.current.contains(target)) return;

      focusModalContent(panelRef.current);
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [isOpen]);


  return (
    <Dialog
      as="div"
      open={isOpen}
      onClose={() => {}} 
      initialFocus={panelRef}
      aria-labelledby={titleId}
      data-testid="logoutNotificationModal-test"
      static
    >
      <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
      <div className="fixed inset-0 z-100 overflow-y-auto bg-black bg-opacity-25" >
        <div className="flex items-center justify-center min-h-full">
          <Dialog.Panel
            ref={panelRef}
            data-testid="logoutNotificationPanel-test"
            tabIndex={-1}
            className="relative bg-white w-[350px] rounded-lg p-[16px] lg:p-[50px] lg:w-[590px] lg:max-w-[590px]"
          >
            <div className="flex flex-col h-full space-y-base mt-sm" role='alert'>
              <Dialog.Title tabIndex={-1} id={titleId} className="text-slate-900 pb-1 text-base lg:text-[32px] lg:pb-4">
                {locales ? locales?.['logoutNotifiction.modal.title'] : 'You have been signed out'}
              </Dialog.Title>
              <Dialog.Description tabIndex={0} className="text-slate-900 text-sm lg:text-lg">
                {locales ?  locales?.['logoutNotifiction.modal.description'] : 'You have been signed out due to inactivity.'}
              </Dialog.Description>

              <div className="text-center mx-auto flex flex-col lg:flex-row-reverse lg:mx-0 lg:text-right">
               

                <Button
                  ref={closeButtonRef}
                  type="button"
                  className="text-center hover:text-white text-[13px] lg:text-base text-sky-700 leading-[17px] w-[204px] lg:w-[104px] h-9 lg:h-12 pl-4 pr-[15px] py-[9px] hover:bg-sky-700 bg-slate-100 cursor-pointer rounded-[100px] lg:round-3xl border outline-0 border-sky-700 lg:mr-3 justify-center gap-2.5 inline-flex  focus:shadow-lg focus:outline-[2px] focus:ring-0"
                  onClick={handleClose}
                >
                  {locales ? locales?.['logoutNotifiction.modal.button.cancel']: 'ok'}
                </Button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
